"use client";

import { Check, ChevronDown, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils/cn";

import { useFixedDropdownPlacement } from "../hooks/use-fixed-dropdown-placement";
import { AGGREGATION_OPTIONS, type ChannelAggregation } from "../schema/datalogger.schema";

type AggregationMultiSelectProps = {
  value: ChannelAggregation[];
  onChange: (next: ChannelAggregation[]) => void;
  "aria-label"?: string;
};

const surfaceClass =
  "ui-control flex min-h-[2.5rem] w-full min-w-[14rem] flex-wrap items-center gap-1 rounded-control px-2 py-1.5 font-sans text-body text-foreground outline-none transition-[border-color,box-shadow,background-color] focus-within:shadow-none";

export function AggregationMultiSelect({ value, onChange, "aria-label": ariaLabel }: AggregationMultiSelectProps) {
  const autoId = useId();
  const listId = `${autoId}-agg-list`;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const placement = useFixedDropdownPlacement(open, anchorRef, "match-anchor");

  useEffect(() => {
    function onDoc(e: globalThis.MouseEvent) {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || menuRef.current?.contains(t)) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function toggle(v: ChannelAggregation) {
    if (value.includes(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  }

  function removeChip(e: ReactMouseEvent, v: ChannelAggregation) {
    e.preventDefault();
    e.stopPropagation();
    onChange(value.filter((x) => x !== v));
  }

  const labelByValue = (v: ChannelAggregation) =>
    AGGREGATION_OPTIONS.find((o) => o.value === v)?.label ?? v;

  const list =
    open && placement ? (
      <ul
        ref={menuRef}
        id={listId}
        role="listbox"
        aria-multiselectable
        style={{
          position: "fixed",
          top: placement.top,
          left: placement.left,
          width: placement.width,
          maxHeight: placement.maxHeight,
          zIndex: 400,
        }}
        className="overflow-auto rounded-control border border-card-border bg-surface-primary py-1 shadow-card"
      >
        {AGGREGATION_OPTIONS.map((o) => {
          const selected = value.includes(o.value);
          return (
            <li key={o.value} role="option" aria-selected={selected}>
              <button
                type="button"
                className={cn(
                  "flex w-full items-center gap-ds-2 px-ds-3 py-ds-2 text-left text-body transition-colors",
                  selected ? "bg-primary/10 text-primary" : "hover:bg-muted-bg/50",
                )}
                onClick={() => toggle(o.value)}
              >
                <span
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                    selected
                      ? "border-primary bg-primary text-white"
                      : "border-border bg-card dark:bg-card",
                  )}
                  aria-hidden
                >
                  {selected ? <Check className="h-2.5 w-2.5 stroke-[3]" /> : null}
                </span>
                {o.label}
              </button>
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <>
      <div className="w-full">
        <div ref={anchorRef} className={surfaceClass}>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {value.length === 0 ? (
              <span className="px-0.5 text-muted">Select aggregators...</span>
            ) : (
              value.map((v) => (
                <span
                  key={v}
                  className="inline-flex max-w-full items-center gap-0.5 rounded-md border border-border/80 bg-card pl-1.5 pr-0.5 text-body font-medium text-foreground shadow-sm dark:bg-muted-bg/50"
                >
                  <span className="max-w-[10rem] truncate">{labelByValue(v)}</span>
                  <button
                    type="button"
                    className="rounded p-0.5 text-muted hover:bg-primary/10 hover:text-primary"
                    aria-label={`Remove ${labelByValue(v)}`}
                    onClick={(e) => removeChip(e, v)}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </span>
              ))
            )}
          </div>
          <button
            type="button"
            className="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted hover:bg-muted-bg/60 hover:text-foreground"
            aria-expanded={open}
            aria-controls={listId}
            aria-label={ariaLabel ?? "Open aggregators list"}
            onClick={() => setOpen((o) => !o)}
          >
            <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} aria-hidden />
          </button>
        </div>
      </div>
      {typeof document !== "undefined" && list ? createPortal(list, document.body) : null}
    </>
  );
}
